
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Taxonomy Codes Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get all taxonomy codes with optional filtering using cursor-based pagination
 * @param {Object} filters - Filters (type, search, active)
 * @param {number} limit - Items per page (max 1000)
 * @param {string} startAfter - Cursor for pagination (optional)
 * @returns {Promise<Object>} Taxonomy codes with cursor-based pagination
 */
export async function getAllTaxonomyCodes(filters = {}, limit = 500, startAfter = null) {
  try {
    console.log('Fetching taxonomy codes with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    // Add startAfter cursor if provided
    if (startAfter) {
      params.append('startAfter', startAfter);
    }
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/referenceData/taxonomy-codes?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      taxonomyCodes: response.data.taxonomyCodes,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching taxonomy codes:', error);
    throw error;
  }
}

/**
 * Get taxonomy codes by type
 * @param {string} taxonomyType - Type of taxonomy codes
 * @param {Object} options - Additional options (activeOnly, search, limit)
 * @returns {Promise<Array>} Array of taxonomy codes
 */
export async function getTaxonomyCodesByType(taxonomyType, options = {}) {
  try {
    console.log('Fetching taxonomy codes by type:', taxonomyType);
    
    const filters = {
      type: taxonomyType,
      active: options.activeOnly !== false ? 'true' : undefined, // Default to active only
      search: options.search
    };
    
    const limit = options.limit || 500;
    
    const result = await getAllTaxonomyCodes(filters, limit);
    return result.taxonomyCodes;
  } catch (error) {
    console.error('Error fetching taxonomy codes by type:', error);
    throw error;
  }
}

/**
 * Get a specific taxonomy code by ID
 * @param {string} codeId - Taxonomy code ID
 * @returns {Promise<Object>} Taxonomy code object
 */
export async function getTaxonomyCodeById(codeId) {
  try {
    console.log('Fetching taxonomy code by ID:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/taxonomy-codes/${codeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.taxonomyCode;
  } catch (error) {
    console.error('Error fetching taxonomy code by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new taxonomy code (admin only)
 * @param {Object} codeData - Taxonomy code data
 * @returns {Promise<Object>} Created taxonomy code
 */
export async function addTaxonomyCode(codeData) {
  try {
    console.log('Creating taxonomy code:', codeData.taxonomy_code);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/referenceData/taxonomy-codes`,
      codeData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating taxonomy code:', error);
    throw error;
  }
}

/**
 * Update an existing taxonomy code (admin only)
 * @param {string} codeId - Taxonomy code ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export async function updateTaxonomyCode(codeId, updateData) {
  try {
    console.log('Updating taxonomy code:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/referenceData/taxonomy-codes/${codeId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating taxonomy code:', error);
    throw error;
  }
}

/**
 * Deactivate a taxonomy code (admin only)
 * @param {string} codeId - Taxonomy code ID
 * @returns {Promise<Object>} Deactivation result
 */
export async function deactivateTaxonomyCode(codeId) {
  try {
    console.log('Deactivating taxonomy code:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/referenceData/taxonomy-codes/${codeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deactivating taxonomy code:', error);
    throw error;
  }
}

/**
 * Bulk import taxonomy codes (admin only)
 * @param {Array} codes - Array of taxonomy code objects
 * @returns {Promise<Object>} Import result
 */
export async function bulkImportTaxonomyCodes(codes) {
  try {
    console.log('Bulk importing taxonomy codes:', codes.length, 'codes');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/referenceData/taxonomy-codes/bulk-import`,
      { codes },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error bulk importing taxonomy codes:', error);
    throw error;
  }
}

/**
 * Search taxonomy codes by type and search term
 * @param {string} taxonomyType - Type of taxonomy codes
 * @param {string} searchTerm - Search term
 * @param {boolean} activeOnly - Whether to include only active codes
 * @returns {Promise<Array>} Array of matching taxonomy codes
 */
export async function searchTaxonomyCodesByType(taxonomyType, searchTerm = '', activeOnly = true) {
  try {
    console.log('Searching taxonomy codes:', taxonomyType, searchTerm);
    
    const filters = {
      type: taxonomyType,
      search: searchTerm,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAllTaxonomyCodes(filters, 500);
    return result.taxonomyCodes;
  } catch (error) {
    console.error('Error searching taxonomy codes:', error);
    throw error;
  }
}

/**
 * Get active taxonomy codes by type (optimized for dropdowns)
 * @param {string} taxonomyType - Type of taxonomy codes
 * @returns {Promise<Array>} Array of active taxonomy codes
 */
export async function getActiveTaxonomyCodesByType(taxonomyType) {
  try {
    return await getTaxonomyCodesByType(taxonomyType, { 
      activeOnly: true,
      limit: 1000
    });
  } catch (error) {
    console.error('Error fetching active taxonomy codes by type:', error);
    throw error;
  }
}

/**
 * Search for taxonomy codes across all types
 * @param {string} searchTerm - Search term
 * @param {boolean} activeOnly - Whether to include only active codes
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} Array of matching taxonomy codes
 */
export async function searchAllTaxonomyCodes(searchTerm, activeOnly = true, limit = 100) {
  try {
    console.log('Searching all taxonomy codes for:', searchTerm);
    
    const filters = {
      search: searchTerm,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAllTaxonomyCodes(filters, limit);
    return result.taxonomyCodes;
  } catch (error) {
    console.error('Error searching all taxonomy codes:', error);
    throw error;
  }
}

/**
 * Get taxonomy code types available in the system
 * @returns {Promise<Array>} Array of unique taxonomy code types
 */
export async function getTaxonomyCodeTypes() {
  try {
    console.log('Fetching taxonomy code types');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/taxonomy-codes/types`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.types;
  } catch (error) {
    console.error('Error fetching taxonomy code types:', error);
    // Fallback to common types if endpoint doesn't exist
    return ['Provider', 'Specialty', 'Certification', 'License'];
  }
}

/**
 * Validate a taxonomy code exists and is active
 * @param {string} taxonomyCode - Taxonomy code to validate
 * @param {string} codeType - Type of taxonomy code
 * @returns {Promise<boolean>} Whether the code is valid and active
 */
export async function validateTaxonomyCode(taxonomyCode, codeType) {
  try {
    const codes = await searchTaxonomyCodesByType(codeType, taxonomyCode, true);
    return codes.some(code => 
      code.taxonomy_code === taxonomyCode && code.active !== false
    );
  } catch (error) {
    console.error('Error validating taxonomy code:', error);
    return false;
  }
}

/**
 * Get provider taxonomy codes (specialized function for healthcare providers)
 * @param {string} specialty - Optional specialty filter
 * @returns {Promise<Array>} Array of provider taxonomy codes
 */
export async function getProviderTaxonomyCodes(specialty = null) {
  try {
    console.log('Fetching provider taxonomy codes');
    
    const filters = {
      type: 'Provider',
      active: 'true'
    };
    
    if (specialty) {
      filters.search = specialty;
    }
    
    const result = await getAllTaxonomyCodes(filters, 1000);
    return result.taxonomyCodes;
  } catch (error) {
    console.error('Error fetching provider taxonomy codes:', error);
    throw error;
  }
}

/**
 * Get specialty taxonomy codes
 * @returns {Promise<Array>} Array of medical specialty taxonomy codes
 */
export async function getSpecialtyTaxonomyCodes() {
  try {
    console.log('Fetching specialty taxonomy codes');
    
    const result = await getTaxonomyCodesByType('Specialty', { 
      activeOnly: true,
      limit: 1000
    });
    
    return result;
  } catch (error) {
    console.error('Error fetching specialty taxonomy codes:', error);
    throw error;
  }
}

/**
 * Search taxonomy codes (simple search across all types)
 * @param {string} searchTerm - Search term
 * @param {boolean} activeOnly - Whether to include only active codes
 * @returns {Promise<Array>} Array of matching taxonomy codes
 */
export async function searchTaxonomyCodes(searchTerm, activeOnly = true) {
  try {
    console.log('Searching taxonomy codes for:', searchTerm);
    return await searchAllTaxonomyCodes(searchTerm, activeOnly, 100);
  } catch (error) {
    console.error('Error searching taxonomy codes:', error);
    throw error;
  }
}

/**
 * Get taxonomy code statistics
 * @returns {Promise<Object>} Statistics about taxonomy codes
 */
export async function getTaxonomyCodeStatistics() {
  try {
    console.log('Fetching taxonomy code statistics');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/taxonomy-codes/statistics`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.statistics;
  } catch (error) {
    console.error('Error fetching taxonomy code statistics:', error);
    // Return empty statistics if endpoint doesn't exist
    return {
      totalCodes: 0,
      activeCount: 0,
      typeBreakdown: {}
    };
  }
}
